import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailPlus, Loader2, Send } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "../App";
import { formatDateSafe } from "../lib/formatDate";
import InvitePreviewDialog from "./InvitePreviewDialog";

export const InvitePatientButton = ({ patient, onInvited, size = "sm", variant = "outline" }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  if (!patient || patient.has_account || !patient.email) return null;
  const already = !!patient.invitation_sent_at;
  const openPreview = () => {
    if (already && !window.confirm(`Une invitation a déjà été envoyée le ${formatDateSafe(patient.invitation_sent_at)}. Renvoyer un nouveau lien (l'ancien sera désactivé) ?`)) return;
    setOpen(true);
  };
  const invite = async (language) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/therapist/patients/${patient.user_id}/invite`, { language }, { withCredentials: true });
      toast.success(`Invitation envoyée à ${r.data.email} (valable 7 jours)`);
      setOpen(false);
      onInvited?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size={size} variant={variant} onClick={openPreview} disabled={busy} className="gap-1.5" title="Prévisualiser puis envoyer l'e-mail permettant au patient de créer son mot de passe" data-testid="invite-patient-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailPlus className="w-4 h-4" />}
        {already ? "Renvoyer l'invitation" : "Inviter à créer son mot de passe"}
      </Button>
      <InvitePreviewDialog open={open} onOpenChange={setOpen} patient={patient} onConfirm={invite} sending={busy} />
    </>
  );
};

export const InviteTestEmailButton = () => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/therapist/patients/invite-test`, {}, { withCredentials: true });
      toast.success(`E-mail d'invitation test envoyé à ${r.data.sent_to} (${r.data.channel.startsWith("smtp_therapeute") ? "via votre SMTP" : "via le serveur"})`, { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi", { duration: 10000 }); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="gap-1.5 text-xs" title="Recevoir un exemplaire de l'e-mail d'invitation sur votre propre adresse" data-testid="invite-test-email-btn">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Tester l'e-mail d'invitation
    </Button>
  );
};

export const InviteAllPatientsButton = ({ count, onDone }) => {
  const [busy, setBusy] = useState(false);
  if (!count) return null;
  const run = async () => {
    if (!window.confirm(`Envoyer un e-mail d'invitation à créer leur mot de passe aux ${count} patient(s) actifs sans compte (jamais invités ou invités il y a plus de 7 jours) ?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/therapist/patients/invite-all`, {}, { withCredentials: true });
      toast.success(`${r.data.sent} invitation(s) envoyée(s)${r.data.errors.length ? ` · ${r.data.errors.length} échec(s)` : ""}`, { duration: 8000 });
      if (r.data.errors.length) toast.error(r.data.errors.slice(0, 3).map((e) => `${e.email} : ${e.detail}`).join(" · "), { duration: 10000 });
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="gap-1.5 text-xs" title="Inviter tous les patients sans compte" data-testid="invite-all-patients-btn">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailPlus className="w-3.5 h-3.5" />} Inviter les {count} sans compte
    </Button>
  );
};
